import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';

const GSAPGrid = () => {
  const containerRef = useRef(null);
  const dotsRef = useRef([]);
  const tlRef = useRef(null);
  const intervalRef = useRef(null);
  const [gridConfig, setGridConfig] = useState({ columns: 0, rows: 0, total: 0 });

  // ── Compute grid dimensions on mount / resize ────────────────────────────
  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current) return;
      const width = document.documentElement.clientWidth;
      const height = window.innerHeight;
      const size = width < 768 ? 40 : 60;
      const columns = Math.ceil(width / size);
      const rows = Math.ceil(height / size);
      setGridConfig({ columns, rows, total: columns * rows });
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── GSAP stagger animation (replaces anime.stagger) ─────────────────────
  useEffect(() => {
    if (gridConfig.total === 0 || dotsRef.current.length === 0) return;

    const dots = dotsRef.current.filter(Boolean);

    const playStagger = () => {
      if (document.hidden) return;

      // Kill previous timeline to avoid overlap
      if (tlRef.current) tlRef.current.kill();

      tlRef.current = gsap.timeline();

      // Phase 1 – pulse outward from centre
      tlRef.current.to(dots, {
        scale: 1.6,
        autoAlpha: 0.85,
        backgroundColor: '#06b6d4', // cyan-500
        duration: 0.4,
        ease: 'power2.out',
        stagger: {
          amount: gridConfig.columns * 0.06, // total stagger time in seconds
          grid: [gridConfig.columns, gridConfig.rows],
          from: 'center',
          ease: 'power1.inOut',
        },
      });

      // Phase 2 – settle back
      tlRef.current.to(dots, {
        scale: 1,
        autoAlpha: 0.06,
        backgroundColor: '#e2e8f0', // slate-200
        duration: 0.9,
        ease: 'expo.out',
        stagger: {
          amount: gridConfig.columns * 0.06,
          grid: [gridConfig.columns, gridConfig.rows],
          from: 'center',
          ease: 'power1.inOut',
        },
      }, '-=0.1');
    };

    // Kick off immediately, then repeat every 5 s
    playStagger();
    intervalRef.current = setInterval(playStagger, 5000);

    return () => {
      clearInterval(intervalRef.current);
      if (tlRef.current) tlRef.current.kill();
    };
  }, [gridConfig]);

  // Collect dot refs
  const setDotRef = (el, i) => {
    dotsRef.current[i] = el;
  };

  if (gridConfig.total === 0) {
    return (
      <div
        ref={containerRef}
        className="absolute inset-0 z-0 h-screen w-full pointer-events-none"
      />
    );
  }

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 z-0 h-screen w-full pointer-events-none flex flex-wrap overflow-hidden"
    >
      {Array.from({ length: gridConfig.total }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-center flex-grow-0 flex-shrink-0"
          style={{
            width: `${100 / gridConfig.columns}%`,
            height: `${100 / gridConfig.rows}%`,
          }}
        >
          {/* Visibility controlled by GSAP autoAlpha — start invisible */}
          <div
            ref={(el) => setDotRef(el, i)}
            className="gsap-grid-dot w-2 h-2 rounded-full"
            style={{ backgroundColor: '#e2e8f0', opacity: 0 }}
          />
        </div>
      ))}
    </div>
  );
};

export default GSAPGrid;
